// Trigger sets — authored per report; see docs/builder-report-authoring-spec.md.
import type { TriggerQuestion } from "./types";
import { ASBESTOS, OCCUPIED, SILICA, MANUAL_HANDLING, FALLING_MATERIAL_PUBLIC, PLANT_PEDESTRIAN } from "./shared";

// Task-specific hazard ids minted in the demolition report JSONs.
const CONCEALED_SERVICES = "haz-electrical-contact-concealed-live-service-during-strip";
const SHARPS = "haz-coshh-sharps-needle-and-biohazard-in-derelict-building";
const PARTY_WALL = "haz-demolition-damage-to-adjacent-property-and-party-wall";

export const DEMOLITION_TRIGGERS: Record<string, TriggerQuestion[]> = {
  "soft-strip": [
    {
      key: "rdSurvey",
      label: "Is a refurbishment & demolition (R&D) asbestos survey held and reviewed before strip?",
      kind: "yesno",
      effects: {
        yes: {
          hazardsOn: [ASBESTOS],
          scopeLine: "R&D asbestos survey (HSG264) reviewed before strip; operatives briefed on recorded ACM locations and the discovery procedure.",
        },
        no: { failCheck: "R&D asbestos survey held and reviewed before strip" },
      },
      notSure: { hazardsOn: [ASBESTOS], failCheck: "R&D asbestos survey held and reviewed before strip" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "rdSurveyRef",
            label: "R&D survey reference / date and surveyor",
            kind: "text",
            placeholder: "e.g. ABC Surveys R&D survey ref 2451, March 2026",
            requiredCheck: "R&D survey reference recorded",
            effects: { "*": { scopeLine: "R&D survey: {answer}." } },
          },
        ],
      },
    },
    {
      key: "isolation",
      label: "Have electrical, gas and water services to the strip area been isolated and proved dead?",
      kind: "yesno",
      effects: {
        yes: {
          hazardsOn: [CONCEALED_SERVICES],
          scopeLine: "Services isolated, locked off and proved dead before strip; any unidentified service treated as live until proven otherwise.",
        },
        no: { hazardsOn: [CONCEALED_SERVICES], failCheck: "Service isolation proved dead before strip" },
      },
      notSure: { hazardsOn: [CONCEALED_SERVICES], failCheck: "Service isolation proved dead before strip" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "isolationRef",
            label: "Isolation confirmation — who issued it and when",
            kind: "text",
            placeholder: "e.g. PC permit-to-work ref 18, electrician's proving-dead record",
            requiredCheck: "Isolation / proving-dead confirmation recorded",
            effects: { "*": { scopeLine: "Service isolation confirmation: {answer}." } },
          },
        ],
      },
    },
    {
      key: "occupied",
      label: "Is any part of the building occupied or open to the public during the strip?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "Building unoccupied and closed to the public during the strip — occupant-interface controls are not required for this task." },
      },
      notSure: { hazardsOn: [OCCUPIED], failCheck: "Occupancy / public access during the works confirmed" },
    },
    {
      key: "cutting",
      label: "Will masonry, screed or tiled finishes be cut, ground or broken out (dust)?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SILICA] },
        no: {
          hazardsOff: [SILICA],
          substancesOff: ["Construction dust"],
          scopeLine: "No cutting, grinding or breaking of masonry or tiled finishes — respirable silica dust controls are not required for this task.",
        },
      },
      notSure: { hazardsOn: [SILICA], failCheck: "Cutting / grinding requirement confirmed" },
    },
    {
      key: "hotWork",
      label: "Will any cutting or grinding generating sparks or heat be carried out?",
      kind: "yesno",
      effects: {
        yes: { permitsAdd: ["Hot work permit"], scopeLine: "Hot works (spark- or heat-generating cutting) under a hot work permit with a fire watch maintained after work ceases." },
        no: { permitsRemove: ["Hot work permit"], scopeLine: "No spark- or heat-generating work — a hot work permit is not required for this task." },
      },
      notSure: { permitsAdd: ["Hot work permit"], failCheck: "Hot work requirement confirmed" },
    },
    {
      key: "derelict",
      label: "Is the building derelict, void or likely to contain sharps, needles or biohazards?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [SHARPS] },
        no: { hazardsOff: [SHARPS], emergencyOff: ["Needlestick"], scopeLine: "Building in managed condition, not derelict — sharps and biohazard controls are not required for this task." },
      },
      notSure: { hazardsOn: [SHARPS], failCheck: "Sharps / biohazard risk confirmed" },
    },
    {
      key: "wasteRoute",
      label: "How will stripped materials be moved off the floors?",
      kind: "choice",
      options: ["Protected waste chute", "Material hoist", "Hand-carry / barrow only"],
      effects: {
        "Protected waste chute": { hazardsOn: [MANUAL_HANDLING], scopeLine: "Waste route: protected chute with barriered drop zone and banksman; loads kept manageable." },
        "Material hoist": { hazardsOn: [MANUAL_HANDLING], scopeLine: "Waste route: material hoist to reduce manual carrying; loads kept manageable." },
        "Hand-carry / barrow only": { hazardsOn: [MANUAL_HANDLING], scopeLine: "Waste route: hand-carry and barrow only — manual handling assessment and operative rotation in place." },
      },
    },
  ],

  "structural-demolition": [
    {
      key: "demolitionPlan",
      label: "Is a written demolition plan and sequence by a competent person held before work starts (CDM 2015 reg 20)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Written demolition plan and BS 6187 sequence by a competent person held before work; operatives and plant operators briefed on it." },
        no: { failCheck: "Written demolition plan / sequence by a competent person confirmed" },
      },
      notSure: { failCheck: "Written demolition plan / sequence by a competent person confirmed" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "competentPersonRef",
            label: "Demolition engineer / competent person who designed the sequence",
            kind: "text",
            placeholder: "e.g. J Bloggs CEng, XYZ Structures, sequence rev B",
            requiredCheck: "Competent person for the demolition sequence named",
            effects: { "*": { scopeLine: "Demolition sequence designed by: {answer}." } },
          },
        ],
      },
    },
    {
      key: "servicesDead",
      label: "Are signed service disconnection certificates held for all supplies before structural work?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Service disconnection certificates held for electricity, gas, water and telecoms before any structural work; no work where a certificate is absent." },
        no: { failCheck: "Service disconnection certificates filed before commencement" },
      },
      notSure: { failCheck: "Service disconnection certificates filed before commencement" },
    },
    {
      key: "method",
      label: "What demolition method will be used?",
      kind: "choice",
      options: ["High-reach machine demolition", "Top-down progressive (small plant on floors)", "Hand demolition with pre-weakening"],
      effects: {
        "High-reach machine demolition": { scopeLine: "Method: high-reach machine demolition with a protected cab; exclusion zone sized to structure height plus machine reach; no persons in the structure during machine work." },
        "Top-down progressive (small plant on floors)": { scopeLine: "Method: top-down progressive demolition with small plant working on the floors, giving tighter control of stability and a smaller exclusion zone." },
        "Hand demolition with pre-weakening": { scopeLine: "Method: controlled hand demolition with engineered pre-weakening only as specified in the sequence; structure kept stable at every stage." },
      },
    },
    {
      key: "partyWall",
      label: "Are there adjacent properties, party walls or attached structures?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PARTY_WALL], requireFields: ["nearbyTradesOrPublic"] },
        no: {
          hazardsOff: [PARTY_WALL],
          emergencyOff: ["Adjacent-structure"],
          scopeLine: "Detached structure with no party walls or attached neighbours — party-wall protection and notices are not required for this task.",
        },
      },
      notSure: { hazardsOn: [PARTY_WALL], failCheck: "Adjacent property / party-wall status confirmed" },
    },
    {
      key: "publicInterface",
      label: "Are neighbouring occupied buildings, footways or roads near the works?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [OCCUPIED, FALLING_MATERIAL_PUBLIC], requireFields: ["nearbyTradesOrPublic"] },
        no: { hazardsOff: [OCCUPIED], scopeLine: "Isolated site with no neighbouring occupied buildings, footways or roads in range — public-interface controls beyond the site exclusion zone are not required." },
      },
      notSure: { hazardsOn: [OCCUPIED, FALLING_MATERIAL_PUBLIC], failCheck: "Public / neighbour interface confirmed" },
    },
    {
      key: "asbestosCleared",
      label: "Has the R&D survey confirmed all ACMs removed (clearance certificates held) before demolition?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [ASBESTOS], scopeLine: "R&D survey complete and all identified ACMs removed under licence with clearance certificates held before structural demolition." },
        no: { hazardsOn: [ASBESTOS], failCheck: "ACM removal / clearance certificates confirmed before demolition" },
      },
      notSure: { hazardsOn: [ASBESTOS], failCheck: "ACM removal / clearance certificates confirmed before demolition" },
    },
    {
      key: "plant",
      label: "Will demolition plant or vehicles operate on site?",
      kind: "yesno",
      effects: {
        yes: { hazardsOn: [PLANT_PEDESTRIAN] },
        no: { hazardsOff: [PLANT_PEDESTRIAN], scopeLine: "No demolition plant or site vehicles in use — plant/pedestrian segregation controls are not required for this method." },
      },
      notSure: { hazardsOn: [PLANT_PEDESTRIAN], failCheck: "Plant / vehicle use confirmed" },
    },
  ],

  "asbestos-awareness": [
    {
      key: "buildingAge",
      label: "Was the building (and the area being worked) constructed before 2000?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Pre-2000 building — asbestos-containing materials are reasonably foreseeable; awareness controls and the discovery procedure apply throughout." },
        no: { scopeLine: "Building constructed after 2000 — ACMs are not reasonably foreseeable, so this RAMS is precautionary only; operatives stay alert for materials reused from older stock and apply the stop-and-report procedure on any suspect find." },
      },
      notSure: { scopeLine: "Building age unconfirmed — treat as pre-2000 and apply asbestos awareness controls in full until confirmed.", failCheck: "Building age / asbestos foreseeability confirmed" },
    },
    {
      key: "rdSurvey",
      label: "Is the R&D survey and asbestos register held and reviewed before work?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "R&D survey (HSG264) and asbestos register reviewed before work; recorded and presumed ACM locations identified and marked." },
        no: { failCheck: "R&D survey and asbestos register held and reviewed before work" },
      },
      notSure: { failCheck: "R&D survey and asbestos register held and reviewed before work" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "registerRef",
            label: "Survey / register reference and date",
            kind: "text",
            placeholder: "e.g. R&D survey ref 2451 (2026), register held in site office",
            requiredCheck: "Survey / register reference recorded",
            effects: { "*": { scopeLine: "Asbestos survey / register: {answer}." } },
          },
        ],
      },
    },
    {
      key: "awarenessTraining",
      label: "Have all operatives completed UKATA/IATP awareness training (refreshed annually)?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "All operatives hold current UKATA/IATP asbestos awareness training, refreshed within the last 12 months." },
        no: { failCheck: "Asbestos awareness training current for all operatives" },
      },
      notSure: { failCheck: "Asbestos awareness training current for all operatives" },
    },
    {
      key: "discoveryProcedure",
      label: "Is the CAR 2012 reg 15 stop-work and reporting procedure briefed and named?",
      kind: "yesno",
      effects: {
        yes: { scopeLine: "Reg 15 discovery procedure briefed to every operative — who stops work, how the area is sealed and who is told — with the discovery report form held on site." },
        no: { failCheck: "Reg 15 discovery / reporting procedure briefed and named" },
      },
      notSure: { failCheck: "Reg 15 discovery / reporting procedure briefed and named" },
      followUps: {
        when: "yes",
        questions: [
          {
            key: "reportTo",
            label: "Who does a discovery get reported to (name / role)?",
            kind: "text",
            placeholder: "e.g. site manager A Smith, then PC asbestos coordinator",
            requiredCheck: "Discovery report contact named",
            effects: { "*": { scopeLine: "Asbestos discovery reported to: {answer}." } },
          },
        ],
      },
    },
  ],
};
